import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

const BEAMER_SCRIPT_SRC = 'https://app.getbeamer.com/js/beamer-embed.js'
const BEAMER_SCRIPT_ID = 'beamer-embed-script'
const BEAMER_SELECTOR_ID = 'beamerSelector'

const BEAMER_SUPPORTED_LANGUAGES = ['en', 'nl', 'fr', 'de']
const BEAMER_FALLBACK_LANGUAGE = 'en'
const getBeamerLanguage = (language: string) =>
    BEAMER_SUPPORTED_LANGUAGES.includes(language) ? language : BEAMER_FALLBACK_LANGUAGE

const BEAMER_ROUTES = [
    { path: '/home', filter: `${import.meta.env.VITE_BEAMER_FILTER};home` },
    { path: '/login', filter: `${import.meta.env.VITE_BEAMER_FILTER};login` },
] as const

declare global {
    interface Window {
        beamer_config?: {
            product_id: string
            language?: string
            filter_by_url?: boolean
            filter?: string
        }
        Beamer?: {
            update?: (params: Partial<Window['beamer_config']>) => void
            destroy?: () => void
        }
    }
}

const matchesPath = (pathname: string, routePath: string) =>
    pathname === routePath || pathname.startsWith(`${routePath}/`)

const getBeamerFilter = (pathname: string) =>
    BEAMER_ROUTES.find((route) => matchesPath(pathname, route.path))?.filter ?? null

const setBeamerVisibility = (visible: boolean) => {
    const el = document.getElementById(BEAMER_SELECTOR_ID)
    if (el) el.style.display = visible ? 'block' : 'none'
}

let beamerLoadPromise: Promise<void> | null = null

const loadBeamer = (initialFilter: string, initialLanguage: string): Promise<void> => {
    if (beamerLoadPromise) return beamerLoadPromise

    window.beamer_config = {
        product_id: import.meta.env.VITE_BEAMER_PRODUCT_ID,
        language: initialLanguage,
        filter_by_url: false,
        filter: initialFilter,
    }

    beamerLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.id = BEAMER_SCRIPT_ID
        script.src = BEAMER_SCRIPT_SRC
        script.type = 'text/javascript'
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Beamer'))
        document.head.appendChild(script)
    })

    return beamerLoadPromise
}

const waitForBeamer = (timeout = 5000): Promise<void> =>
    new Promise((resolve) => {
        if (window.Beamer?.update) {
            resolve()
            return
        }
        const start = Date.now()
        const id = setInterval(() => {
            if (window.Beamer?.update || Date.now() - start > timeout) {
                clearInterval(id)
                resolve()
            }
        }, 50)
    })

const useBeamer = () => {
    const { pathname } = useLocation()
    const { i18n } = useTranslation()
    const language = getBeamerLanguage(i18n.language)

    useEffect(() => {
        let cancelled = false
        const filter = getBeamerFilter(pathname)

        if (!filter) {
            setBeamerVisibility(false)
            return
        }

        const run = async () => {
            await loadBeamer(filter, language)
            if (cancelled) return
            await waitForBeamer()
            if (cancelled) return
            window.Beamer?.update?.({ filter, language })
            setBeamerVisibility(true)
        }

        run().catch(() => setBeamerVisibility(false))

        const observer = new MutationObserver(() => setBeamerVisibility(!!getBeamerFilter(pathname)))
        observer.observe(document.body, { childList: true, subtree: true })

        return () => {
            cancelled = true
            observer.disconnect()
        }
    }, [pathname, language])
}

export default useBeamer
